import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Mail, ExternalLink, FileText, MapPin } from "lucide-react";
import { MaskedLines, Reveal, ChapterHeading } from "@/components/motion";
import JsonLd from "@/components/JsonLd";
import { STATES } from "@/constants/states";
import { EMAIL } from "@/constants/site";
import { usePageMeta } from "@/hooks/usePageMeta";

const STEPS = [
  { n: "01", t: "Shielding design before construction", b: "A qualified physicist calculates every barrier — walls, floor, ceiling, doors, and windows — for your exact equipment, layout, workload, and adjacent occupancy." },
  { n: "02", t: "Register with the state", b: "Radiation machines are registered with the state radiation-control program, usually before or as operations begin." },
  { n: "03", t: "Build and install per the design", b: "Your contractor builds to the material specifications in the shielding report — no guesswork at the job site." },
  { n: "04", t: "Plan review and final checks", b: "Where the state or local authority requires it, the report goes in for plan review; a post-installation survey closes the loop where required." },
];

const StateRequirements = () => {
  const { slug } = useParams();
  const s = STATES.find((x) => x.slug === slug);
  usePageMeta(
    s ? `X-Ray Shielding Requirements in ${s.name} | Heartland Physics` : "Shielding Requirements | Heartland Physics",
    s
      ? `${s.name} radiation machine registration and shielding design requirements — plain-language guidance from a medical health physicist.`
      : "State-by-state X-ray shielding requirement guidance."
  );

  if (!s) {
    return (
      <section className="pt-16">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8">
          <p className="eyebrow">Not found</p>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-ink">We haven't published that state guide yet.</h1>
          <Link to="/shielding-requirements" data-testid="state-req-404-back" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blueprint">
            <ArrowLeft size={15} /> All state guides
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <JsonLd
        id={`state-faq-${s.slug}`}
        data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            {
              "@type": "Question",
              name: `Do I need to register an X-ray machine in ${s.name}?`,
              acceptedAnswer: { "@type": "Answer", text: s.quickAnswer },
            },
            {
              "@type": "Question",
              name: `Do I need a shielding design in ${s.name}?`,
              acceptedAnswer: {
                "@type": "Answer",
                text: `Most new, relocated, remodeled, or upgraded imaging rooms in ${s.name} need a shielding review prepared by a qualified physicist before construction or equipment installation. Requirements vary by project and jurisdiction — the state radiation-control program has the final word.`,
              },
            },
          ],
        }}
      />

      <section data-testid="state-req-page" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
          <Reveal y={12}>
            <Link to="/shielding-requirements" data-testid="state-req-back" className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-ink/50 transition-colors hover:text-blueprint">
              <ArrowLeft size={14} className="transition-transform duration-200 group-hover:-translate-x-1" /> All states
            </Link>
            <p className="eyebrow mt-8">Shielding requirements — {s.name}</p>
          </Reveal>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl">
            <MaskedLines delay={0.1} lines={[`X-ray shielding`, `requirements in ${s.name}.`]} />
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg">
              Plain-language guidance for imaging facilities in {s.name} — what to file, who regulates it, and where a shielding design fits in the process.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <Reveal>
                <p className="eyebrow">The short answer</p>
                <div className="mt-5 border-l-2 border-blueprint bg-paper p-6" data-testid="state-req-short-answer">
                  <p className="text-base leading-relaxed text-ink/80">{s.quickAnswer}</p>
                  <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/45">
                    From our {s.name} registration guide
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.1}>
                <h2 className="mt-12 font-heading text-lg font-extrabold tracking-tight text-ink">The usual order of operations</h2>
                <div className="mt-6 flex flex-col gap-px border border-border bg-border">
                  {STEPS.map((step) => (
                    <div key={step.n} className="flex gap-5 bg-white p-5">
                      <span className="font-mono text-xs text-blueprint">{step.n}</span>
                      <div>
                        <p className="font-heading text-sm font-bold text-ink">{step.t}</p>
                        <p className="mt-1 text-sm leading-relaxed text-ink/60">{step.b}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="flex flex-col gap-6">
              <Reveal delay={0.08}>
                <div className="border border-border bg-white p-6" data-testid="state-req-resources">
                  <div className="flex items-center gap-3">
                    <MapPin size={17} className="text-blueprint" />
                    <h2 className="font-heading text-sm font-extrabold tracking-tight text-ink">Official {s.name} resources</h2>
                  </div>
                  <div className="mt-5 flex flex-col gap-2.5">
                    {s.links.map((l) => (
                      <a
                        key={l.href}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`state-req-link-${l.text.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
                        className="group flex items-center justify-between border border-border px-4 py-3 text-sm font-medium text-ink/75 transition-colors hover:border-blueprint hover:text-blueprint"
                      >
                        <span className="flex items-center gap-2.5">
                          {l.href.endsWith(".pdf") ? <FileText size={15} className="text-blueprint" /> : <ExternalLink size={15} className="text-blueprint" />}
                          {l.text}
                        </span>
                        <ArrowUpRight size={14} className="opacity-0 transition-opacity group-hover:opacity-100" />
                      </a>
                    ))}
                  </div>
                </div>
              </Reveal>

              <Reveal delay={0.14}>
                <Link
                  to={`/insights/${s.guideSlug}`}
                  data-testid="state-req-guide-link"
                  className="group flex items-center justify-between border border-blueprint/30 bg-blueprint/5 p-5 transition-colors hover:bg-blueprint/10"
                >
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50">Step-by-step guide</p>
                    <p className="mt-1.5 font-heading text-sm font-bold text-ink">How to register an X-ray machine in {s.name}</p>
                  </div>
                  <ArrowRight size={16} className="shrink-0 text-blueprint transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Reveal>

              {s.extraGuides.length > 0 && (
                <Reveal delay={0.18}>
                  <div className="border border-border bg-white p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-ink/50">Local guides in {s.name}</p>
                    <div className="mt-3 flex flex-col gap-2">
                      {s.extraGuides.map((g) => (
                        <Link
                          key={g.slug}
                          to={`/insights/${g.slug}`}
                          data-testid={`state-req-extra-${g.slug.slice(0, 30)}`}
                          className="group flex items-center justify-between px-1 py-2 text-sm font-medium text-ink/75 transition-colors hover:text-blueprint"
                        >
                          {g.name}
                          <ArrowRight size={14} className="text-blueprint opacity-0 transition-opacity group-hover:opacity-100" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}

              <Reveal delay={0.22}>
                <div className="relative overflow-hidden border border-ink bg-ink p-7 text-white">
                  <div className="spotlight-dark absolute inset-0" />
                  <p className="relative font-mono text-xs uppercase tracking-[0.28em] text-white/70">Project in {s.name}?</p>
                  <p className="relative mt-3 font-heading text-base font-bold tracking-tight">
                    $950 per room. Report in five business days. 90-day money-back guarantee.
                  </p>
                  <div className="relative mt-6 flex flex-wrap gap-3">
                    <Link
                      to="/request"
                      data-testid="state-req-cta-request"
                      className="group flex items-center gap-2 bg-white px-5 py-3 text-sm font-semibold text-ink transition-colors duration-200 hover:bg-azure hover:text-white"
                    >
                      Request a Shielding Design
                      <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <a
                      href={`mailto:${EMAIL}`}
                      data-testid="state-req-cta-email"
                      className="flex items-center gap-2 border border-white/30 px-5 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-azure hover:text-azure"
                    >
                      <Mail size={15} /> Email Lancer@HeartlandPhysics.com
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <p className="mt-10 max-w-2xl text-xs leading-relaxed text-ink/45">
            Requirements change and vary by project. The current rules of the responsible radiation-control authority always control — we confirm them for your project as part of every design.
          </p>
        </div>
      </section>
    </>
  );
};

export default StateRequirements;
